<template>
  <div class="login-otp-form">
    <div class="heading">
        <h3 class="mb-s">{{ $t('title.loginOtp') }}</h3>
        <div class="sub-heading line-height-s">
          <span v-if="verifiedPhone">
          {{ $t('title.loginOtpSubtitle') }}
          </span>
          <span v-else>
          {{ $t('title.loginOtpSubtitleWithoutPhone') }}
          </span>
        </div>
    </div>
    <p class="username-show">
      <span class="text-bold">{{ email}} </span>
      <bf-link underline=""><a
        class="text-decoration"
        href="#changeAccount"
        @click.prevent="changeAccount"
      >{{ $t("common.changeAccount") }}</a></bf-link>
    </p>
    <bf-form
      :disabled="isLoading"
      :ref="formName"
      class="mt-xs"
      :model="form"
      :rules="formRules"
      @submit.native.prevent="onSubmit">
      <bf-form-item prop="loginOtp" :label="$t('form.loginOtp')">
        <bf-input
          ref="input"
          :loading="isLoading"
          v-model="form.loginOtp"
          :maxlength=6
          @on-enter="onSubmit"
          :placeholder="$t('form.placeholderOtp')"
        />
      </bf-form-item>
      <!-- Resend OTP -->
      <div v-if="timer > 0">
        {{ $t('common.notreceivedAOtp')}}
        <span>
          {{ $t("common.resendPasswordAfter", { "second": timer } ) }}
        </span>
      </div>
      <p v-else-if="verifiedPhone" class="otp-method">
        {{ $t('form.otpBy')}}
        <bf-link>
          <a href="#" @click.prevent="getOtpBySMS">SMS</a>
        </bf-link>
        <span>
          {{ $t("form.or").toLowerCase() }}
        </span>
        <bf-link>
          <a href="#" @click.prevent="getOtpByVoice">VOICE</a>
        </bf-link>
      </p>
      <!-- End Resend OTP -->
      <!--begin::Gap-->
      <div class="gap pb-l" />
      <!--end::Gap-->
      <bf-button
        type="primary"
        class="btn-2xl text-m"
        block
        @click.native.stop.prevent="onSubmit"
      >
        {{ $t("form.confirmOtp")}}
        <bf-icon name="arrow-forward" />
      </bf-button>
    </bf-form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { sendOtp, confirmOtp } from '@/services/otp';
import { setToken } from '@/utils/common';
import userServices from '@/services/user';
import RULES from '@/config/rules';

export default {
  name: 'LoginOtpComponent',
  props: {
    email: {
      type: String,
      default: '',
    },
    verifiedPhone: {
      type: Boolean,
      default: true,
    },
    count: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      apiAccessToken: process.env.VUE_APP_API_ACCESS_TOKEN_KEY || 'tmp_token',
      isLoading: false,
      timeout: null,
      timer: 0,
      formName: 'loginOtp',
      otpType: 'secretkey',
      form: {
        loginOtp: '',
      },
    };
  },
  computed: {
    ...mapGetters('device', [
      'isMobile',
    ]),
    formRules() {
      return {
        loginOtp: [
          {
            required: true,
            message: this.$t('form.pleaseInput',
            { name: this.$t('form.loginOtp').toLowerCase() }),
            trigger: ['change'],
          },
          {
            pattern: RULES.PATTERN_NUMBER,
            message: this.$t('form.validator.numeric',
            { field: this.$t('form.loginOtp') }),
            trigger: ['change'],
          },
          {
            min: 6,
            message: this.$t('form.validator.min', { field: this.$t('form.loginOtp'), min: '6' }),
            trigger: ['change'],
          },
        ],
      };
    },
  },
  watch: {
    count(nextValue, oldValue) {
      if (nextValue !== oldValue) {
        this.$refs.input?.$el?.querySelector('input').focus();
      }
    },
  },
  mounted() {
    this.countdownTimer(0);
  },
  destroyed() {
    clearInterval(this.timeout);
  },
  methods: {
    async onSubmit() {
      this.$refs[this.formName].validate(async (valid) => {
        if (valid) {
          try {
            this.isLoading = true;
            const payload = { code: this.form.loginOtp };
            const response = await confirmOtp(payload);
            // Set login token
            setToken(response?.data && response.data[this.apiAccessToken]);
            this.$notify({
              title: this.$t('notification.success.title'),
              message: response?.message,
              type: 'success',
              customClass: 'success',
            });
            this.getUserInfo();
          } finally {
            this.isLoading = false;
          }
        }
      });
    },
    async getUserInfo() {
      try {
        const res = await userServices.userInfo();
        this.$emit('goToStep', 'final-step');
      } catch (error) {
        const errorCode = error?.response?.data?.error;
        if (['NeedChangePasswordException', 'PasswordExpired'].includes(errorCode)) {
          this.$emit('updateMessage', error?.response?.data?.detail);
          this.$emit('goToStep', 'change-password-step');
        }
        await new Promise((r) => setTimeout(r, 300));
        this.isLoading = false;
      }
    },
    async getOtpBySMS() {
      const otpType = 'SMS';
      const response = await this.getOtp(otpType);
      const time = response?.retry_after ?? 0;
      this.countdownTimer(time);
    },
    async getOtpByVoice() {
      const otpType = 'VOICE';
      const response = await this.getOtp(otpType);
      const time = response?.retry_after ?? 0;
      this.countdownTimer(time);
    },
    async getOtp(otpType = 'secretkey') {
      try {
        this.isLoading = true;
        const payload = { verify_type: otpType };
        const response = await sendOtp(payload);
        this.$notify({
          title: this.$t('notification.success.title'),
          message: response?.message,
          type: 'success',
          customClass: 'success',
        });
        return response;
      } finally {
        this.isLoading = false;
      }
    },
    changeAccount() {
      this.$emit('goToStep', 'step-1');
    },
    countdownTimer(time = 30) {
      this.timer = time;
      this.timeout = setInterval(() => {
        if (this.timer > 0) {
          this.timer -= 1;
        } else {
          clearInterval(this.timeout);
        }
      }, 1000);
    }, // end countdownTimer
  },
};
</script>
